import React, { useState, useEffect } from "react"
import { MdClose } from "react-icons/md"
import { searchEntities } from "../../../api"
import { useMsal } from "@azure/msal-react";
import { getToken } from "../../../authConfig";
import { useDebounce } from "../../../hooks/useDebounce"
import { SearchEntity } from "../../../api/models"

interface UserSearchBoxProps {
    onSelectEntity?: (entity: SearchEntity) => void
    selectedEntities: SearchEntity[]
    onRemoveEntity: (entityId: string) => void
}

export const UserSearchBox: React.FC<UserSearchBoxProps> = ({
    onSelectEntity,
    selectedEntities,
    onRemoveEntity
}) => {
    const [searchQuery, setSearchQuery] = useState("")
    const [results, setResults] = useState<SearchEntity[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const client = useMsal().instance;
    const debounced = useDebounce(searchQuery, 300)

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)

    const fetchEntities = async (query: string) => {
        const token = client ? await getToken(client) : undefined;
        setIsLoading(true)
        try {
            setResults(await searchEntities(query, token?.accessToken))
        } catch (err) {
            console.error("searchEntities failed:", err)
            setResults([])
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (!debounced.trim()) {
            setResults([])
            setIsLoading(false)
            return
        }
        fetchEntities(debounced) // trigger on each debounced keystroke
    }, [debounced])

    const handleEntityClick = (entity: SearchEntity) => {
        if (selectedEntities.some(se => se.id === entity.id)) return
        onSelectEntity?.(entity)
        setSearchQuery("")
        setResults([])
    }

    const handleEntityRemove = (entityId: string) => onRemoveEntity(entityId)

    return (
        <div className="w-full">
            <div className="user-search-box p-2 border border-gray-400 rounded-md flex flex-wrap items-center min-h-[48px] focus-within:border-eq-green focus-within:ring-eq-green focus-within:ring-1">
                {selectedEntities.map(entity => (
                    <div
                        key={entity.id}
                        className="flex items-center bg-eq-light-grey text-eq-grey rounded-full px-3 py-1 mr-2"
                    >
                        <span className="text-sm">{entity.displayName}</span>
                        <button
                            onClick={() => handleEntityRemove(entity.id)}
                            className="ml-1 text-eq-grey hover:text-eq-grey"
                        >
                            <MdClose size={16} />
                        </button>
                    </div>
                ))}
                <input
                    type="text"
                    placeholder="Search for users or groups..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="flex-grow py-1 px-2 appearance-none border-none outline-none bg-transparent focus:ring-0 focus:border-0"
                />
            </div>
            {isLoading ? (
                <div>Loading...</div>
            ) : (
                results.length > 0 && (
                    <ul className="user-list mt-2 bg-eq-white border border-eq-light-grey rounded-md shadow-md max-h-60 overflow-y-auto">
                        {results.map(entity => (
                            <li
                                key={entity.id}
                                onClick={() => handleEntityClick(entity)}
                                className="cursor-pointer p-3 border-b border-eq-light-grey last:border-0 hover:bg-eq-super-light-grey"
                            >
                                <span>{entity.displayName}</span>
                                <span className="text-eq-grey text-sm ml-2">
                                    {entity.objectType === "user" ? entity.mail : "Group"}
                                </span>
                            </li>
                        ))}
                    </ul>
                )
            )}
        </div>
    )
}
